import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { VehiclesService } from 'src/app/shared/api-services/vehicles.service';
import { CommunicationService } from 'src/app/shared/utilities/comunication.service';
import { DateUtilsService } from 'src/app/shared/utilities/date-utils.service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { InputDatePickerComponent } from 'src/app/shared/components/inputs/input-date-picker/input-date-picker.component';
import { VehicleDisable } from 'src/app/shared/models/vehicle-disable/vehicle-disable';
import { Subject, takeUntil } from 'rxjs';
import { VehicleDisableRequest } from 'src/app/shared/models/vehicle-disable/vehicle-disable-request';
import { ModalUtilsService } from 'src/app/shared/utilities/modal-utils.service';
import { AutocompleteModule } from '../../../inputs/autocomplete-primeng/autocomplete.module';
import { UserGroup } from 'src/app/shared/models/user-group/user-group';

@Component({
    selector: 'vehicle-disable-modal',
    imports: [
        CommonModule,
        TranslateModule,
        ReactiveFormsModule,
        InputDatePickerComponent,
        AutocompleteModule
    ],
    templateUrl: './vehicle-disable-modal.component.html',
    styleUrls: ['./vehicle-disable-modal.component.scss']
})

export class VehicleDisableModalComponent implements OnInit, OnDestroy {

  @Input() vehicleId: string;
  @Input() vehicleDisableId: string;

  form: FormGroup;
  selectedUserGroups: UserGroup[] = [];

  private _destroy$ = new Subject<void>();

  constructor(
    public bsModalRef: BsModalRef,
    private _vehiclesService: VehiclesService,
    private _dateUtilsService: DateUtilsService,
    private _communicationService: CommunicationService,
    private _modalUtilsService: ModalUtilsService
  ) {
    this.form = new FormGroup({
      disableFrom: new FormControl(null, [Validators.required]),
      disableFromTime: new FormControl(null, [Validators.required]),
      disableTo: new FormControl(null),
      disableToTime: new FormControl(null),
      note: new FormControl(null),
      userGroupIds: new FormControl([])
    });
  }

  ngOnInit(): void {
    if (this.vehicleDisableId) {
      this.getVehicleDisable();
    }
  }

  private getVehicleDisable() {
    this._vehiclesService.getVehicleDisable$(this.vehicleId, this.vehicleDisableId, {
      includeUserGroups: true
    })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (vehicleDisable) => {
          this.setValues(vehicleDisable);
        }
      });
  }

  private setValues(vehicleDisable: VehicleDisable) {
    if (vehicleDisable) {
      let disableFrom = vehicleDisable.disabledFrom ? new Date(vehicleDisable.disabledFrom) : null;
      let disabledFromTime = disableFrom ? this._dateUtilsService.parseDateToTimeString(disableFrom) : null;
      let disableTo = vehicleDisable.disabledTo ? new Date(vehicleDisable.disabledTo) : null;
      let disabledToTime = disableTo ? this._dateUtilsService.parseDateToTimeString(disableTo) : null;

      this.form.patchValue({
        disableFrom: disableFrom,
        disableFromTime: disabledFromTime,
        disableTo: disableTo,
        disableToTime: disabledToTime,
        note: vehicleDisable.note,
        userGroupIds: vehicleDisable.userGroupIds || []
      });

      if (vehicleDisable.userGroups && vehicleDisable.userGroups.length > 0) {
        this.selectedUserGroups = [...vehicleDisable.userGroups];
      }
    }
  }

  onUserGroupSelected(group: UserGroup): void {
    if (!this.selectedUserGroups.some(g => g.id === group.id)) {
      this.selectedUserGroups.push(group);
      this.updateUserGroupIds();
    }
  }

  removeUserGroup(groupId: string): void {
    this.selectedUserGroups = this.selectedUserGroups.filter(group => group.id !== groupId);
    this.updateUserGroupIds();
  }

  private updateUserGroupIds(): void {
    const userGroupIds = this.selectedUserGroups.map(group => group.id);
    this.form.patchValue({ userGroupIds });
  }

  onSubmit() {
    if (!this.form.valid) {
      return;
    }

    const request: VehicleDisableRequest = {
      disabledFrom: null,
      note: this.form.value.note,
      userGroupIds: this.form.value.userGroupIds
    };

    let disabledFromString: string = null;
    let disabledToString: string = null;

    if (this.form.value.disableFrom && this.form.value.disableFromTime) {
      disabledFromString = this._dateUtilsService.combineDateAndTime(this.form.value.disableFrom, this.form.value.disableFromTime);
      request.disabledFrom = disabledFromString;
    }

    if (this.form.value.disableTo && this.form.value.disableToTime) {
      disabledToString = this._dateUtilsService.combineDateAndTime(this.form.value.disableTo, this.form.value.disableToTime);
      request.disabledTo = disabledToString;
    }

    // Utilizzo ModalUtilsService per aprire la modale di verifica
    this._modalUtilsService.openVehicleUsageCheckModal(this.vehicleId, disabledFromString, disabledToString)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (proceed: boolean) => {
          if (proceed) {
            this.processSuspension(request);
          }
        }
      });
  }

  private processSuspension(request: VehicleDisableRequest): void {
    if (this.vehicleDisableId) {
      this._vehiclesService.updateVehicleDisable$(this.vehicleId, this.vehicleDisableId, request)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response) => {
            this.onUpdated();
          }
        });
    } 
    else {
      this._vehiclesService.createVehicleDisable$(this.vehicleId, request)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response) => {
            this.onUpdated();
        }
      });
    }
  }

  private onUpdated() {
    this._communicationService.sendEvent();
    this.bsModalRef.hide();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
